import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { getAdminUsers } from '../../../services/admin-service';
import { User as BaseUser } from '../../../types';
import { formatPhone } from '../../../utils/formatters';

// Extend the base User type with the admin-specific fields
interface AdminUser extends BaseUser {
  is_approved: boolean;
}

// Paginated response interface
interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

const AdminUsersPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);

  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    open: false,
    title: '',
    message: '',
    action: async () => {
      /* intentionally empty */
    },
  });

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Usuários', path: '/admin/users' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, tabValue]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        // Use the admin service for regular users (paginated response)
        console.log('Fetching admin users with page:', page + 1, 'rowsPerPage:', rowsPerPage);
        const response = await getAdminUsers(page + 1, rowsPerPage);
        console.log('Admin users response structure:', {
          responseExists: Boolean(response),
          hasItems: response && 'items' in response,
          items: response?.items,
          total: response?.total,
        });

        if (response && response.items && Array.isArray(response.items)) {
          console.log('Setting users with response items (length):', response.items.length);
          setUsers(response.items as AdminUser[]);
          setTotalItems(response.total);
        } else {
          console.error('Invalid response format from getAdminUsers:', response);
          setUsers([]);
          setTotalItems(0);
        }
      } else if (tabValue === 1) {
        // For pending users, use the direct API with pagination
        const url = `/users/pending?skip=${page * rowsPerPage}&limit=${rowsPerPage}&sort=created_at:desc`;
        console.log('Fetching pending users from URL:', url);

        const pendingResponse = await api.get<PaginatedResponse<AdminUser>>(url);
        console.log('Pending users response structure:', {
          responseExists: Boolean(pendingResponse),
          hasItems: pendingResponse && 'items' in pendingResponse,
          items: pendingResponse?.items,
          total: pendingResponse?.total,
        });

        if (pendingResponse && pendingResponse.items && Array.isArray(pendingResponse.items)) {
          console.log('Setting users with pending items (length):', pendingResponse.items.length);
          setUsers(pendingResponse.items);
          setTotalItems(pendingResponse.total);
        } else {
          console.error('Invalid data format for pending users:', pendingResponse);
          setUsers([]);
          setTotalItems(0);
        }
      } else if (tabValue === 2) {
        // For rejected users, use the direct API with pagination
        const url = `/users/rejected?skip=${page * rowsPerPage}&limit=${rowsPerPage}&sort=created_at:desc`;
        console.log('Fetching rejected users from URL:', url);

        const rejectedResponse = await api.get<PaginatedResponse<AdminUser>>(url);
        console.log('Rejected users response structure:', {
          responseExists: Boolean(rejectedResponse),
          hasItems: rejectedResponse && 'items' in rejectedResponse,
          items: rejectedResponse?.items,
          total: rejectedResponse?.total,
        });

        if (rejectedResponse && rejectedResponse.items && Array.isArray(rejectedResponse.items)) {
          console.log('Setting users with rejected items (length):', rejectedResponse.items.length);
          setUsers(rejectedResponse.items);
          setTotalItems(rejectedResponse.total);
        } else {
          console.error('Invalid data format for rejected users:', rejectedResponse);
          setUsers([]);
          setTotalItems(0);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Falha ao carregar usuários', { variant: 'error' });
      setUsers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  // Create user avatar from initials
  const getInitials = (name: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get appropriate color for user role
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'reseller':
        return 'info';
      default:
        return 'default';
    }
  };

  // Format date in a user-friendly way
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Approve user
  const approveUser = async (userId: string) => {
    try {
      await api.patch<AdminUser, null>(`/users/${userId}/approve`, null);
      enqueueSnackbar('Usuário aprovado com sucesso', { variant: 'success' });
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error approving user:', error);
      enqueueSnackbar('Falha ao aprovar usuário', { variant: 'error' });
    }
  };

  // Reject user
  const rejectUser = async (userId: string) => {
    try {
      await api.patch<AdminUser, null>(`/users/${userId}/reject`, null);
      enqueueSnackbar('Usuário rejeitado com sucesso', { variant: 'success' });
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting user:', error);
      enqueueSnackbar('Falha ao rejeitar usuário', { variant: 'error' });
    }
  };

  const handleApproveClick = (user: AdminUser) => {
    setConfirmDialog({
      open: true,
      title: 'Aprovar Usuário',
      message: `Deseja aprovar o acesso de ${user.name} (${user.email})?`,
      action: async () => approveUser(user.id),
    });
  };

  const handleRejectClick = (user: AdminUser) => {
    setConfirmDialog({
      open: true,
      title: 'Rejeitar Usuário',
      message: `Deseja rejeitar o acesso de ${user.name} (${user.email})? Esta ação irá desativar a conta.`,
      action: async () => rejectUser(user.id),
    });
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = async () => {
    await confirmDialog.action();
    handleConfirmDialogClose();
  };

  return (
    <Box>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ mb: 3 }}
      >
        <Tab label="Todos os Usuários" />
        <Tab
          label="Pendentes de Aprovação"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: 'error.main',
              color: 'white',
            },
          }}
        />
        <Tab label="Usuários Rejeitados" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px"></TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Função</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Criado em</TableCell>
                  {tabValue === 1 && <TableCell align="center">Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {users && users.length > 0 ? (
                  users.map(user => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Avatar
                          sx={{ bgcolor: user.role === 'admin' ? 'primary.main' : 'info.main' }}
                        >
                          {getInitials(user.name || '')}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.name || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{formatPhone(user.phone)}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role === 'admin' ? 'Administrador' : 'Revendedor'}
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            tabValue === 2
                              ? 'Rejeitado'
                              : !user.is_active
                                ? 'Inativo'
                                : user.is_approved
                                  ? 'Ativo'
                                  : 'Pendente'
                          }
                          color={
                            tabValue === 2
                              ? 'error'
                              : !user.is_active
                                ? 'error'
                                : user.is_approved
                                  ? 'success'
                                  : 'warning'
                          }
                          size="small"
                          sx={tabValue === 2 ? { backgroundColor: '#d32f2f', color: 'white' } : {}}
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.created_at || '')}</TableCell>
                      {tabValue === 1 && (
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleApproveClick(user)}
                            >
                              Aprovar
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleRejectClick(user)}
                            >
                              Rejeitar
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={tabValue === 1 ? 7 : 6} align="center">
                      {tabValue === 0
                        ? 'Nenhum usuário encontrado'
                        : tabValue === 1
                          ? 'Não há usuários pendentes de aprovação'
                          : 'Não há usuários rejeitados'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogClose}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.title.includes('Aprovar') ? 'primary' : 'error'}
            variant="contained"
            autoFocus
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsersPage;
